import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { API, api, errMsg, isSessionError, formatRupiah } from "@/lib/api";
import { playAlert, soundEnabled, setSoundEnabled, unlockAudio } from "@/lib/sound";

const LABEL = { topup: "Top Up", bongkar: "Bongkar" };
const describe = (kind, l) =>
  kind === "topup" ? `${l.product_name} · ${l.game_id} · ${l.chips} · ${formatRupiah(l.price)}` : `${l.product_name} · ${l.game_id} · ${l.chip_amount} · ${formatRupiah(l.amount_rp || 0)}`;

export const useLiveFeed = (onLogout) => {
  const [feed, setFeed] = useState({});
  const prev = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("rk_token");
    if (!token) {
      return undefined;
    }
    const es = new EventSource(`${API}/admin/stream?token=${encodeURIComponent(token)}`);
    es.onmessage = (e) => {
      const snap = JSON.parse(e.data);
      Object.entries(snap).forEach(([kind, v]) => {
        const before = prev.current?.[kind];
        if (prev.current && v.latest && v.latest.id !== before?.latest?.id) {
          playAlert(kind);
          toast(`${LABEL[kind]} baru masuk`, { description: describe(kind, v.latest) });
          window.dispatchEvent(new CustomEvent("jp:tx-new", { detail: { kind } }));
        } else if (prev.current && (v.pending !== before?.pending || v.unseen !== before?.unseen)) {
          window.dispatchEvent(new CustomEvent("jp:tx-new", { detail: { kind } }));
        }
      });
      prev.current = snap;
      setFeed(snap);
    };
    es.addEventListener("logout", () => {
      es.close();
      onLogout();
    });
    return () => es.close();
  }, [onLogout]);

  useEffect(() => {
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    return () => window.removeEventListener("pointerdown", unlockAudio);
  }, []);

  return feed;
};

export const SoundToggle = () => {
  const [on, setOn] = useState(soundEnabled);
  const toggle = () => {
    setSoundEnabled(!on);
    setOn(!on);
    if (!on) playAlert("topup");
  };
  return (
    <button onClick={toggle} className="rk-btn-ghost inline-flex items-center gap-1 whitespace-nowrap text-xs" data-testid="sound-toggle" aria-pressed={on}>
      {on ? <Volume2 size={13} /> : <VolumeX size={13} />} <span className="hidden sm:inline">Bunyi {on ? "ON" : "OFF"}</span>
    </button>
  );
};

export const NewBadge = ({ show, id }) =>
  show ? (
    <span className="mr-2 inline-block animate-pulse rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] font-black text-black" data-testid={`new-badge-${id}`}>NEW</span>
  ) : null;

const NEW_MS = 5000;

export const useLiveRows = (url, kind) => {
  const [rows, setRows] = useState([]);
  const [newIds, setNewIds] = useState(() => new Set());
  const flagged = useRef(new Set());
  const timers = useRef([]);

  const load = useCallback(() => {
    api.get(url).then((r) => {
      setRows(r.data.items);
      const fresh = r.data.items.filter((x) => !x.seen_at && !flagged.current.has(x.id)).map((x) => x.id);
      if (fresh.length) {
        fresh.forEach((id) => flagged.current.add(id));
        setNewIds((s) => new Set([...s, ...fresh]));
        timers.current.push(setTimeout(() => {
          setNewIds((s) => new Set([...s].filter((id) => !fresh.includes(id))));
        }, NEW_MS));
      }
    }).catch((e) => !isSessionError(e) && toast.error(errMsg(e)));
  }, [url]);

  const markSeen = useCallback((game) => {
    const ids = rows.filter((x) => !x.seen_at && (game === "all" || x.product_id === game)).map((x) => x.id);
    if (!ids.length) {
      return;
    }
    const now = new Date().toISOString();
    setRows((rs) => rs.map((x) => (ids.includes(x.id) ? { ...x, seen_at: now } : x)));
    api.post("/admin/seen", { kind, ids }).catch(() => {});
  }, [rows, kind]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    load();
    const onNew = (e) => {
      if (e.detail.kind === kind) {
        load();
      }
    };
    window.addEventListener("jp:tx-new", onNew);
    return () => window.removeEventListener("jp:tx-new", onNew);
  }, [load, kind]);

  return { rows, newIds, load, markSeen };
};
